import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Manages custom required-credentials for a single agency. Any therapist
 * who has at least one visit at this agency picks up these requirements
 * automatically in the credential audit + weekly compliance digest.
 */
export function AgencyRequiredCredentials({ agencyId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    if (!agencyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/agency-profiles/${agencyId}/required-credentials`);
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load agency requirements');
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (reqId) => {
    if (!window.confirm('Remove this required credential from this agency?')) return;
    try {
      await axios.delete(`${API}/agency-profiles/${agencyId}/required-credentials/${reqId}`);
      toast.success('Requirement removed');
      load();
    } catch (e) {
      toast.error('Failed to remove requirement');
    }
  };

  return (
    <div className="space-y-3" data-testid="agency-required-credentials">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-sm flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" />
            Required Credentials ({rows.length})
          </h4>
          <p className="text-xs text-muted-foreground">
            Applied automatically to anyone with visits at this agency.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowAdd(true)} data-testid="add-agency-required-btn">
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>

      {loading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      {rows.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Credential</th>
                <th className="text-left px-3 py-2 font-medium">Blocks scheduling</th>
                <th className="text-left px-3 py-2 font-medium">Notes</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-medium">{r.document_type}</td>
                  <td className="px-3 py-2">
                    {r.blocks_scheduling ? (
                      <Badge className="text-[10px] bg-red-100 text-red-700">Yes</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{r.notes || '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(r.id)}
                      className="text-destructive hover:text-destructive"
                      title="Remove"
                      data-testid={`remove-agency-required-${r.id}`}
                    >
                      <Trash2 size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-muted-foreground border rounded-lg p-4 text-center">
          No custom required credentials for this agency.
        </div>
      )}

      {showAdd && (
        <AddAgencyRequiredModal
          agencyId={agencyId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}


function AddAgencyRequiredModal({ agencyId, onClose, onSaved }) {
  const [docType, setDocType] = useState('');
  const [blocks, setBlocks] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!docType.trim()) {
      toast.error('Credential name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/agency-profiles/${agencyId}/required-credentials`, {
        document_type: docType.trim(),
        blocks_scheduling: blocks,
        notes: notes.trim() || null,
      });
      toast.success('Requirement added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add requirement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-medium text-lg">Add Required Credential</h3>
          <p className="text-sm text-muted-foreground">
            Required for anyone with at least one visit at this agency.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Credential name</label>
            <Input
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              placeholder="e.g. Background Check"
              data-testid="agency-required-doctype-input"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={blocks}
              onChange={(e) => setBlocks(e.target.checked)}
            />
            <span className="flex items-center gap-1">
              <AlertCircle size={13} className="text-amber-500" />
              Block scheduling when missing (off by default)
            </span>
          </label>

          <div>
            <label className="text-xs font-medium block mb-1">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Why this agency requires it…"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="agency-required-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AgencyRequiredCredentials;
